<template>
  <donut-selector-prefabs-user
    v-if="sessionTagsRequireThisRole || roleSelectionsRequireThisRole"
    v-model="user"
    :special-placeholder="roleSelectionPlaceholder"
    :allow-clear="canBeCleared"
    @onFocus="onFocus"
    @onBlur="onBlur"
  />
</template>

<script setup>
  import { computed, onUnmounted } from 'vue';
  import { useStore } from 'vuex';

  const { state, getters, commit } = useStore();

  const props = defineProps({
    session: { type: Object, required: true },
    role: { type: Object, required: true },
  });
  const emit = defineEmits(['onPaste']);

  // Display Logic
  const tags = computed(() => getters['onboardingManualEnrollment/tagsForSession'](props.session));
  const sessionTagsRequireThisRole = computed(() => {
    // Always display the primary role
    if (props.role.purpose === 'primary') return true;
    // Else, display the role if any of the tags in the row have this role as an involved role
    return tags.value.some((tag) => tag.roleIds?.some(roleId => roleId === props.role.id));
  });
  const roleSelectionsRequireThisRole = computed(() => props.session.memberships.some((membership) => membership.role_selection?.selector_role_id === props.role.id));

  const membership = computed(() => props.session.memberships.find(m => m.role.id === props.role.id));

  // `user`
  const user = computed({
    get() {
      return membership.value?.user;
    },
    set(selection) {
      commit('onboardingManualEnrollment/updateMembership', {
        sessionId: props.session.id,
        roleId: props.role.id,
        key: 'user',
        value: selection,
      });
    },
  });

  // `role_selection`
  const roleSelectionPlaceholder = computed(() => {
    // Some memberships will have a `role_selection` instead of a `user`, implying that someone else will be picking the user for the membership
    // In this instance, we display a purple "special placeholder" to indicate who will be making the pick, but the user can override this should they want.
    const roleSelection = membership.value?.role_selection;
    if (roleSelection) return roleSelection.placeholder;
  });

  // Clearing Value
  const canBeCleared = computed(() => !!(user.value || roleSelectionPlaceholder.value));

  // Event Handlers
  const onPaste = (e) => {
    const pastedText = e.clipboardData.getData('text');
    if (pastedText.includes('\t') || pastedText.includes('\n')) {
      // If the text includes either \t or \n, they're pasting spreadsheet data. Prevent the real paste and handle
      // in the table itself via the emit.
      e.preventDefault();
      emit('onPaste', { pastedText, session: props.session, role: props.role });
    }
  };
  const onFocus = () => {
    window.addEventListener('paste', onPaste);
  };
  const onBlur = () => {
    window.removeEventListener('paste', onPaste);
  };

  // Lifecycle Hooks
  onUnmounted(() => {
    window.removeEventListener('paste', onPaste);
  });
</script>
